/**
 * AgentComposer — Lumière · Cinematic AI Workflow Director.
 *
 * Feature set:
 *   • Cinematic hero layout (uppercase headline · pill chips · lime cta)
 *   • Style presets dropdown that biases the LLM (cinematográfico, anime, …)
 *   • Reference image drop zone (drag any image → uploaded to R2 → LLM
 *     receives a mood/palette hint referencing the URL)
 *   • Prompt history: last 5 goals stored in localStorage, one-click reload
 *   • Microphone dictation via Web Speech API (free browser API, no STT
 *     backend) — hold or toggle to speak, transcript flows into the goal
 *
 * The rest of the flow (needsInputs / preview / applying) is unchanged.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Sparkles, Loader2, X, ArrowRight, AlertTriangle,
  Check, FileText, Image as ImageIcon, Upload, RefreshCw, Info, Plus,
  Camera, Clapperboard, Video, ImagePlay, Wand2, Mic, MicOff, History, Trash2,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { agentService } from '../../services/agentService';
import { getProjects, createProject as apiCreateProject } from '../../services/projectService';
import { uploadToR2 } from '../../utils/uploadToR2';
import { useWorkflowStore } from '../../store/workflowStore';
import { useRevealStore } from '../../store/revealStore';
import FlowsSidePanel from './FlowsSidePanel';
import { useTypewriter } from '../../hooks/useTypewriter';

// Sugerencias que se escriben y se borran solas dentro del campo mientras
// esté vacío. Se adaptan a lo que el usuario eligió arriba (Imagen / Video).
const IDEAS = {
  imagen: [
    'Mejorar una fotografía',
    'Crear una campaña visual para mi empresa',
    'Diseñar una publicación para las redes',
  ],
  video: [
    'Crear un anuncio corto donde pasa lo siguiente',
    'Crear un video siguiendo este guion',
    'Crear un video avatar con mi imagen',
  ],
};
import { useCreditsStore } from '../../store/creditsStore';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition';
import { promptHistory } from '../../utils/promptHistory';

const EXAMPLE_GOALS = [
  'Restaurar una foto vieja y hacerla más grande',
  'Crear un video corto donde mi personaje saluda a cámara',
  'Convertir este texto en audio con voz natural',
  'Un avatar hablando el guion que tengo',
  'Video de mi mascota bailando con música alegre',
];

const PROVIDER_META = {
  openai:    { label: 'GPT-5.2',            hint: 'rápido' },
  anthropic: { label: 'Claude Sonnet 4.5',  hint: 'detallado' },
};

const STYLE_PRESETS = [
  { id: 'auto',            label: 'Auto',            hint: 'Lumière decide' },
  { id: 'cinematografico', label: 'Cinematográfico', hint: 'planos amplios, luz cálida, grano sutil' },
  { id: 'anime',           label: 'Anime',           hint: 'línea limpia, colores saturados estilo Studio Ghibli' },
  { id: 'documental',      label: 'Documental',      hint: 'crudo, cámara en mano, natural' },
  { id: 'cyberpunk',       label: 'Cyberpunk',       hint: 'neón, high contrast, teal & magenta' },
  { id: 'painterly',       label: 'Pictórico',       hint: 'pinceladas visibles, óleo, textura' },
  { id: 'retro8mm',        label: 'Retro 8mm',       hint: 'película vintage, grano fuerte, sepia' },
  { id: 'minimalista',     label: 'Minimalista',     hint: 'fondo plano, composición limpia, poca paleta' },
];

const AgentComposer = ({ open, onClose, variant = 'modal' }) => {
  const isEntry = variant === 'entry';
  // ── State ──────────────────────────────────────────
  const [step, setStep] = useState('compose');
  const [goal, setGoal] = useState('');
  const [provider, setProvider] = useState('openai');
  const [target, setTarget] = useState('new');
  const [targetProjectId, setTargetProjectId] = useState('');
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [mediaHint, setMediaHint] = useState('video');
  const [stylePreset, setStylePreset] = useState('auto');

  // Reference image (mood/palette hint)
  const [referenceImage, setReferenceImage] = useState(null); // { url, name }
  const [uploadingRef, setUploadingRef] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Prompt history
  const [history, setHistory] = useState([]);

  const [sessionId, setSessionId] = useState(null);
  const [plan, setPlan] = useState(null);
  const [neededInputs, setNeededInputs] = useState([]);
  const [providedInputs, setProvidedInputs] = useState({});
  const [uploadingInputs, setUploadingInputs] = useState({});
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const [refineText, setRefineText] = useState('');
  const [refining, setRefining] = useState(false);
  const [previewView, setPreviewView] = useState('timeline'); // 'timeline' | 'list'

  const [openMenu, setOpenMenu] = useState(null); // 'provider' | 'target' | 'style' | 'history' | null

  const setNodes = useWorkflowStore((s) => s.setNodes);
  const setEdges = useWorkflowStore((s) => s.setEdges);
  const loadProject = useWorkflowStore((s) => s.loadProject);

  const balance = useCreditsStore((s) => s.balance);

  // ── Mic (Web Speech API) ───────────────────────────
  const {
    supported: micSupported,
    listening: micListening,
    transcript: micTranscript,
    start: micStart,
    stop: micStop,
    reset: micReset,
    error: micError,
  } = useSpeechRecognition({ lang: 'es-AR', interim: true });

  // Sugerencias que se escriben y se borran solas dentro del campo: sólo
  // mientras esté vacío, sin dictado en curso y con el composer a la vista.
  const ideaHint = useTypewriter(IDEAS[mediaHint] || IDEAS.video, {
    active: open && step === 'compose' && !goal && !micListening,
  });

  // Baseline captured when the user starts dictating, so we can append
  // (rather than replace) any text the user had already typed.
  const micBaselineRef = useRef('');

  useEffect(() => {
    if (!micListening) return;
    if (!micTranscript) return;
    const base = micBaselineRef.current;
    const separator = base && !base.endsWith(' ') && !base.endsWith('\n') ? ' ' : '';
    setGoal(`${base}${separator}${micTranscript}`);
  }, [micTranscript, micListening]);

  useEffect(() => {
    if (micError) toast.error(`Micrófono: ${micError}`);
  }, [micError]);

  const toggleMic = () => {
    if (!micSupported) {
      toast.error('Tu navegador no soporta dictado por voz. Usá Chrome, Edge o Safari.');
      return;
    }
    if (micListening) {
      micStop();
    } else {
      micBaselineRef.current = goal;
      micReset();
      micStart();
    }
  };

  // Load projects when opening
  useEffect(() => {
    if (!open) return;
    setProjectsLoading(true);
    getProjects()
      .then((res) => setProjects(res.projects || res || []))
      .catch((e) => toast.error(`No se pudo cargar proyectos: ${e.message}`))
      .finally(() => setProjectsLoading(false));
    // Refresh history whenever the modal opens
    setHistory(promptHistory.list());
  }, [open]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setStep('compose');
      setGoal('');
      setPlan(null);
      setNeededInputs([]);
      setProvidedInputs({});
      setErrorMsg(null);
      setSessionId(null);
      setRefineText('');
      setOpenMenu(null);
      setReferenceImage(null);
      setStylePreset('auto');
      if (micListening) micStop();
    }
  }, [open]);

  // Outside-click close for chip menus
  useEffect(() => {
    if (!openMenu) return;
    const handler = (e) => {
      if (!e.target.closest('.lumiere-chip-pill') && !e.target.closest('.lumiere-chip-menu')) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openMenu]);

  const insufficient = plan && balance !== null && plan.estimated_credits > balance;

  // ── Reference image handlers ───────────────────────
  const handleRefImageFile = async (file) => {
    if (!file || !file.type?.startsWith('image/')) {
      toast.error('Arrastrá una imagen (jpg, png, webp).');
      return;
    }
    setUploadingRef(true);
    try {
      const { url } = await uploadToR2(file);
      setReferenceImage({ url, name: file.name });
      toast.success('Referencia visual cargada.');
    } catch (e) {
      toast.error(`No se pudo subir la referencia: ${e.message}`);
    } finally {
      setUploadingRef(false);
    }
  };

  const handleDragOver = (e) => {
    if (e.dataTransfer?.types?.includes('Files')) {
      e.preventDefault();
      setIsDragging(true);
    }
  };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleRefImageFile(file);
  };

  const stylePresetLabel = STYLE_PRESETS.find((p) => p.id === stylePreset)?.label || 'Auto';

  // ── Handlers ───────────────────────────────────────
  const handleGenerate = async (extraInputs = null) => {
    setBusy(true);
    setErrorMsg(null);
    try {
      // Compose the goal with hints so the LLM biases correctly.
      const mediaTail = mediaHint === 'imagen'
        ? '(Resultado esperado: IMAGEN estática — optimizar el flujo para producir una imagen final, no video.)'
        : '(Resultado esperado: VIDEO — el flujo debe terminar en un video.)';
      const styleObj = STYLE_PRESETS.find((p) => p.id === stylePreset);
      const styleTail = styleObj && styleObj.id !== 'auto'
        ? `(Estilo visual: ${styleObj.label} — ${styleObj.hint}. Reflejalo en los prompts de los nodos.)`
        : '';
      const refTail = referenceImage
        ? `(Referencia estética: ${referenceImage.url} — reproducí sus colores, mood, textura y encuadre.)`
        : '';
      const composedGoal = [goal, mediaTail, styleTail, refTail].filter(Boolean).join('\n\n');

      const res = await agentService.planWorkflow({
        goal: composedGoal,
        provided_inputs: extraInputs || undefined,
        provider,
        session_id: sessionId || undefined,
      });
      const p = res.plan;
      if (!sessionId && p) {
        setSessionId(`agent-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
      }
      // Save the raw user goal to history (not the composed one)
      promptHistory.push(goal);
      setHistory(promptHistory.list());

      if (p.status === 'needs_inputs') {
        setNeededInputs(p.required_inputs || []);
        setPlan(p);
        setStep('needsInputs');
      } else {
        setPlan(p);
        setStep('preview');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Error desconocido';
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleUploadInput = async (inputId, file) => {
    if (!file) return;
    setUploadingInputs((s) => ({ ...s, [inputId]: true }));
    try {
      const { url } = await uploadToR2(file);
      setProvidedInputs((s) => ({ ...s, [inputId]: url }));
      toast.success(`"${file.name}" agregado como ${inputId}`);
    } catch (e) {
      toast.error(`Falló la subida: ${e.message}`);
    } finally {
      setUploadingInputs((s) => ({ ...s, [inputId]: false }));
    }
  };

  const allInputsReady = useMemo(
    () => neededInputs.every((ri) => providedInputs[ri.id]),
    [neededInputs, providedInputs]
  );

  const handleContinueWithInputs = () => handleGenerate(providedInputs);

  const handleRefine = async () => {
    if (!refineText.trim() || !plan) return;
    setRefining(true);
    setErrorMsg(null);
    try {
      const res = await agentService.refineWorkflow({
        plan: { nodes: plan.nodes, edges: plan.edges },
        instruction: refineText,
        provider,
        session_id: sessionId || undefined,
      });
      setPlan(res.plan);
      setRefineText('');
      toast.success('Flujo refinado.');
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Error refinando';
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setRefining(false);
    }
  };

  const handleApply = async () => {
    if (!plan) return;
    setBusy(true);
    setStep('applying');
    try {
      if (target === 'existing') {
        if (!targetProjectId) throw new Error('Elegí un proyecto de destino.');
        await loadProject(targetProjectId);
      } else {
        const created = await apiCreateProject({
          name: goal.slice(0, 60) || 'Flujo generado con IA',
          description: plan.explanation?.slice(0, 200),
        });
        const newId = created.id || created.project?.id;
        if (!newId) throw new Error('No se pudo crear el proyecto.');
        await loadProject(newId);
      }

      const state = useWorkflowStore.getState();
      const existingIds = new Set(state.nodes.map((n) => n.id));

      const prefix = target === 'existing' ? `ai_${Date.now().toString(36).slice(-4)}_` : '';
      const idMap = {};
      const rfNodes = plan.nodes.map((n) => {
        const finalId = prefix ? `${prefix}${n.id}` : (existingIds.has(n.id) ? `${prefix || 'ai_'}${n.id}` : n.id);
        idMap[n.id] = finalId;
        return {
          id: finalId,
          type: n.type,
          position: n.position || { x: 80, y: 80 },
          data: { label: n.data?.label || n.type, ...(n.data || {}) },
        };
      });
      const rfEdges = plan.edges.map((e, i) => ({
        id: `agent-e-${Date.now()}-${i}`,
        source: idMap[e.source] || e.source,
        target: idMap[e.target] || e.target,
        sourceHandle: e.sourceHandle,
        targetHandle: e.targetHandle,
      }));

      if (target === 'existing') {
        setNodes([...state.nodes, ...rfNodes]);
        setEdges([...state.edges, ...rfEdges]);
      } else {
        setNodes(rfNodes);
        setEdges(rfEdges);
      }

      // El workflow ya está completo, configurado y válido en el store: lo
      // único que se agrega acá es cómo se REVELA visualmente (capa aparte,
      // ver store/revealStore.js). Si falla, el flujo ya está utilizable.
      try {
        useRevealStore.getState().start({
          nodeIds: rfNodes.map((n) => n.id),
          edgeIds: rfEdges.map((e) => e.id),
        });
      } catch (revealErr) {
        console.warn('[Lumiere] no se pudo animar la aparición:', revealErr);
      }

      toast.success('Flujo aplicado en el canvas.');
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Error aplicando';
      setErrorMsg(msg);
      toast.error(msg);
      setStep('preview');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const currentProjectName = projects.find((p) => p.id === targetProjectId)?.name;
  const targetLabel = target === 'new' ? 'Proyecto nuevo' : (currentProjectName || 'Elegir…');

  return (
    <div
      className={`lumiere-backdrop ${isEntry ? 'is-entry' : ''}`}
      onClick={onClose}
      data-testid={isEntry ? 'lumiere-entry-overlay' : undefined}
    >
      <div
        className={`lumiere-modal ${isEntry ? 'is-entry' : ''}`}
        onClick={(e) => e.stopPropagation()}
        data-testid="agent-composer"
      >
        <div className="lumiere-bg" aria-hidden />
        <div className="lumiere-bg-grain" aria-hidden />

        <button
          className="lumiere-close"
          onClick={onClose}
          aria-label="Cerrar"
          data-testid="lumiere-close"
        >
          <X size={16} />
        </button>

        {/* ── Step: Compose (cinematic hero) ────────── */}
        {step === 'compose' && (
          <div className={isEntry ? 'lumiere-entry-grid' : 'lumiere-compose-wrap'}>
          <div className="lumiere-hero">
            {isEntry ? (
              <>
                <div className="lumiere-brandrow">
                  <span className="lumiere-brandtile"><Sparkles size={20} /></span>
                  <div>
                    <div className="lumiere-brandname">
                      Lumière <em className="lumiere-badge-new">NUEVO</em>
                    </div>
                    <span className="lumiere-brandsub">
                      Agente Creativo de Studio Media Labs
                    </span>
                  </div>
                </div>
                <div className="lumiere-eyebrow is-entry">CREACIÓN SIMPLE CON LUMIÈRE</div>
                <h1 className="lumiere-headline lumiere-headline-entry">
                  Hola, soy Lumière.
                  <span className="lumiere-headline-grad">¿Qué creamos hoy?</span>
                </h1>
                <p className="lumiere-sub lumiere-sub-entry">
                  Contame tu idea en lenguaje natural y la convierto en un
                  proyecto listo para usar.
                </p>
              </>
            ) : (
              <>
              <div className="lumiere-eyebrow">
                <Sparkles size={13} /> LUMIÈRE · AGENTE CREATIVO
              </div>
                <h1 className="lumiere-headline">DIRIGE CUALQUIER COSA QUE IMAGINES</h1>
                <p className="lumiere-sub">
                  Describe tu idea y Lumière arma el flujo de nodos que la haga real.
                  Convertí guiones en escenas, mezclá personajes, sonidos y movimiento en minutos.
                </p>
              </>
            )}

            {/* Top pill chips row */}
            <div className="lumiere-chips-top">
              {/* Motor */}
              <div className="lumiere-chip-slot">
                <button
                  className={`lumiere-chip-pill ${openMenu === 'provider' ? 'is-open' : ''}`}
                  onClick={() => setOpenMenu(openMenu === 'provider' ? null : 'provider')}
                  data-testid="lumiere-chip-provider"
                >
                  <span className="dot green" />
                  <span className="label">Motor</span>
                  <b className="value">{PROVIDER_META[provider].label}</b>
                </button>
                {openMenu === 'provider' && (
                  <div className="lumiere-chip-menu">
                    {Object.entries(PROVIDER_META).map(([id, meta]) => (
                      <button
                        key={id}
                        className={`lumiere-menu-item ${provider === id ? 'active' : ''}`}
                        onClick={() => { setProvider(id); setOpenMenu(null); }}
                      >
                        <b>{meta.label}</b>
                        <span>{meta.hint}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <span className="lumiere-chip-divider" />

              {/* Destino */}
              <div className="lumiere-chip-slot">
                <button
                  className={`lumiere-chip-pill ${openMenu === 'target' ? 'is-open' : ''}`}
                  onClick={() => setOpenMenu(openMenu === 'target' ? null : 'target')}
                  data-testid="lumiere-chip-target"
                >
                  <FileText size={13} />
                  <span className="label">Destino</span>
                  <b className="value">{targetLabel}</b>
                </button>
                {openMenu === 'target' && (
                  <div className="lumiere-chip-menu wide">
                    <button
                      className={`lumiere-menu-item ${target === 'new' ? 'active' : ''}`}
                      onClick={() => { setTarget('new'); setTargetProjectId(''); setOpenMenu(null); }}
                      data-testid="lumiere-target-new"
                    >
                      <b><Plus size={11} /> Proyecto nuevo</b>
                      <span>Crea un canvas limpio</span>
                    </button>
                    <div className="lumiere-menu-section">Proyecto existente</div>
                    {projectsLoading && <div className="lumiere-menu-empty">Cargando…</div>}
                    {!projectsLoading && projects.length === 0 && (
                      <div className="lumiere-menu-empty">Sin proyectos</div>
                    )}
                    {projects.map((p) => (
                      <button
                        key={p.id}
                        className={`lumiere-menu-item ${target === 'existing' && targetProjectId === p.id ? 'active' : ''}`}
                        onClick={() => { setTarget('existing'); setTargetProjectId(p.id); setOpenMenu(null); }}
                      >
                        <b>{p.name || p.id}</b>
                        <span>{p.nodes_count || 0} nodo(s)</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <span className="lumiere-chip-divider" />

              {/* Estilo — real presets dropdown */}
              <div className="lumiere-chip-slot">
                <button
                  className={`lumiere-chip-pill ${openMenu === 'style' ? 'is-open' : ''}`}
                  onClick={() => setOpenMenu(openMenu === 'style' ? null : 'style')}
                  data-testid="lumiere-chip-style"
                >
                  <Camera size={13} />
                  <span className="label">Estilo</span>
                  <b className="value">{stylePresetLabel}</b>
                </button>
                {openMenu === 'style' && (
                  <div className="lumiere-chip-menu wide">
                    {STYLE_PRESETS.map((preset) => (
                      <button
                        key={preset.id}
                        className={`lumiere-menu-item ${stylePreset === preset.id ? 'active' : ''}`}
                        onClick={() => { setStylePreset(preset.id); setOpenMenu(null); }}
                        data-testid={`lumiere-style-${preset.id}`}
                      >
                        <b>{preset.label}</b>
                        <span>{preset.hint}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Historial */}
              {history.length > 0 && (
                <>
                  <span className="lumiere-chip-divider" />
                  <div className="lumiere-chip-slot">
                    <button
                      className={`lumiere-chip-pill ${openMenu === 'history' ? 'is-open' : ''}`}
                      onClick={() => setOpenMenu(openMenu === 'history' ? null : 'history')}
                      data-testid="lumiere-chip-history"
                    >
                      <History size={13} />
                      <span className="label">Historial</span>
                      <b className="value">{history.length}</b>
                    </button>
                    {openMenu === 'history' && (
                      <div className="lumiere-chip-menu wide">
                        <div className="lumiere-menu-section">
                          <span>Últimos prompts</span>
                          <button
                            className="lumiere-menu-clear"
                            onClick={(e) => { e.stopPropagation(); promptHistory.clear(); setHistory([]); setOpenMenu(null); }}
                            title="Borrar historial"
                          >
                            <Trash2 size={11} /> Borrar
                          </button>
                        </div>
                        {history.map((h) => (
                          <button
                            key={h}
                            className="lumiere-menu-item history"
                            onClick={() => { setGoal(h); setOpenMenu(null); }}
                            data-testid="lumiere-history-item"
                          >
                            <span className="history-text">{h}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Composer bar */}
            <div
              className={`lumiere-composer ${isDragging ? 'is-dropping' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="lumiere-tabs">
                <button
                  type="button"
                  className={`lumiere-tab ${mediaHint === 'imagen' ? 'active' : ''}`}
                  onClick={() => setMediaHint('imagen')}
                  data-testid="lumiere-tab-imagen"
                >
                  <ImagePlay size={16} />
                  <span>Imagen</span>
                </button>
                <button
                  type="button"
                  className={`lumiere-tab ${mediaHint === 'video' ? 'active' : ''}`}
                  onClick={() => setMediaHint('video')}
                  data-testid="lumiere-tab-video"
                >
                  <Video size={16} />
                  <span>Video</span>
                </button>
              </div>

              <div className="lumiere-composer-main">
                {/* Reference image thumbnail (if any) */}
                {(referenceImage || uploadingRef) && (
                  <div className="lumiere-ref-chip" data-testid="lumiere-ref-chip">
                    {uploadingRef ? (
                      <>
                        <Loader2 className="spin" size={13} />
                        <span>Subiendo referencia…</span>
                      </>
                    ) : (
                      <>
                        <img src={referenceImage.url} alt="referencia" />
                        <span className="ref-name" title={referenceImage.name}>{referenceImage.name}</span>
                        <span className="ref-badge">Mood ref</span>
                        <button
                          type="button"
                          className="ref-remove"
                          onClick={() => setReferenceImage(null)}
                          aria-label="Quitar referencia"
                          data-testid="lumiere-ref-remove"
                        >
                          <X size={11} />
                        </button>
                      </>
                    )}
                  </div>
                )}

                <div className="lumiere-textarea-row">
                  <textarea
                    className="lumiere-textarea"
                    rows={2}
                    placeholder={ideaHint
                      ? `${ideaHint}…`
                      : 'Describe tu escena: usá lenguaje natural, o pegá un guion completo.'}
                    value={goal}
                    onChange={(e) => setGoal(e.target.value)}
                    autoFocus
                    data-testid="agent-goal-input"
                  />
                  <button
                    type="button"
                    className={`lumiere-mic-btn ${micListening ? 'listening' : ''} ${!micSupported ? 'unsupported' : ''}`}
                    onClick={toggleMic}
                    title={
                      !micSupported
                        ? 'Dictado no soportado en este navegador (usá Chrome/Edge/Safari)'
                        : (micListening ? 'Detener dictado' : 'Dictar por voz')
                    }
                    aria-label={micListening ? 'Detener dictado' : 'Iniciar dictado'}
                    data-testid="lumiere-mic-btn"
                  >
                    {micListening ? <MicOff size={16} /> : <Mic size={16} />}
                    {micListening && <span className="mic-pulse" aria-hidden />}
                  </button>
                </div>

                <div className="lumiere-action-row">
                  <label className="lumiere-ref-upload-btn" title="Arrastrá una imagen o hacé click">
                    <ImageIcon size={13} />
                    <span>Referencia visual</span>
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => handleRefImageFile(e.target.files?.[0])}
                      data-testid="lumiere-ref-input"
                    />
                  </label>
                  <div className="lumiere-quick-chips">
                    {EXAMPLE_GOALS.slice(0, 4).map((eg) => (
                      <button
                        key={eg}
                        type="button"
                        className="lumiere-quick-chip"
                        onClick={() => setGoal(eg)}
                        title={eg}
                      >
                        {eg.length > 34 ? `${eg.slice(0, 34)}…` : eg}
                      </button>
                    ))}
                  </div>
                </div>

                {errorMsg && (
                  <div className="lumiere-error"><AlertTriangle size={13} /> {errorMsg}</div>
                )}
              </div>

              <button
                type="button"
                className="lumiere-generate"
                onClick={() => handleGenerate()}
                disabled={busy || goal.trim().length < 3 || (target === 'existing' && !targetProjectId)}
                data-testid="agent-generate"
              >
                {busy ? (
                  <>
                    <Loader2 className="spin" size={18} />
                    <span>GENERANDO…</span>
                  </>
                ) : isEntry ? (
                  <>
                    <Sparkles size={17} className="star" />
                    <span>Crear con Lumière</span>
                    <ChevronRight size={17} />
                  </>
                ) : (
                  <>
                    <span>GENERAR</span>
                    <Sparkles size={18} className="star" />
                  </>
                )}
              </button>

              {/* Drop overlay (shown while dragging a file over the composer) */}
              {isDragging && (
                <div className="lumiere-drop-overlay" aria-hidden>
                  <ImageIcon size={26} />
                  <span>Soltá la imagen para usarla como referencia</span>
                </div>
              )}
            </div>
          </div>
          {isEntry && <FlowsSidePanel onExit={onClose} />}
          {isEntry && (
            <footer className="lumiere-entry-foot">
              Dos formas de crear. Un mismo poder creativo.
            </footer>
          )}
          </div>
        )}

        {/* ── Step: Needs Inputs ─────────────────────── */}
        {step === 'needsInputs' && (
          <div className="lumiere-panel">
            <div className="lumiere-panel-head">
              <h2><Info size={16} /> Lumière necesita algunos assets</h2>
              <p>{plan?.explanation || 'Cargá los assets para completar el flujo.'}</p>
            </div>
            <div className="lumiere-inputs-list">
              {neededInputs.map((ri) => (
                <div className="lumiere-input-item" key={ri.id}>
                  <div className="lumiere-input-meta">
                    <ImageIcon size={14} />
                    <div>
                      <b>{ri.label}</b>
                      <span>{ri.why}</span>
                    </div>
                  </div>
                  {providedInputs[ri.id] ? (
                    <div className="lumiere-input-ok">
                      <img src={providedInputs[ri.id]} alt={ri.label} />
                      <button
                        className="lumiere-btn ghost"
                        onClick={() => setProvidedInputs((s) => { const c = { ...s }; delete c[ri.id]; return c; })}
                      >
                        Cambiar
                      </button>
                    </div>
                  ) : (
                    <label className="lumiere-upload-btn">
                      {uploadingInputs[ri.id]
                        ? <><Loader2 className="spin" size={12} /> Subiendo…</>
                        : <><Upload size={12} /> Subir imagen</>}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => handleUploadInput(ri.id, e.target.files?.[0])}
                      />
                    </label>
                  )}
                </div>
              ))}
            </div>

            {errorMsg && <div className="lumiere-error"><AlertTriangle size={13} /> {errorMsg}</div>}
            <div className="lumiere-panel-actions">
              <button className="lumiere-btn secondary" onClick={() => setStep('compose')}>← Volver</button>
              <button
                className="lumiere-btn primary"
                onClick={handleContinueWithInputs}
                disabled={!allInputsReady || busy}
                data-testid="agent-continue-inputs"
              >
                {busy ? <><Loader2 className="spin" size={14} /> Generando…</> : <><ArrowRight size={14} /> Continuar</>}
              </button>
            </div>
          </div>
        )}

        {/* ── Step: Preview ──────────────────────────── */}
        {step === 'preview' && plan && (
          <div className="lumiere-panel">
            <div className="lumiere-panel-head">
              <h2><Clapperboard size={16} /> Flujo propuesto</h2>
              <p>{plan.explanation}</p>
            </div>

            <div className="lumiere-plan-stats">
              <span><b>{plan.nodes.length}</b> nodos</span>
              <span><b>{plan.edges.length}</b> conexiones</span>
              <span className={insufficient ? 'bad' : 'good'}>
                <b>{plan.estimated_credits}</b> créditos est.
              </span>
              <span className="lumiere-hint">Balance: {balance ?? '…'}</span>

              <div className="lumiere-view-toggle" role="tablist" aria-label="Vista del flujo">
                <button
                  type="button"
                  className={previewView === 'timeline' ? 'active' : ''}
                  onClick={() => setPreviewView('timeline')}
                  data-testid="lumiere-view-timeline"
                  role="tab"
                  aria-selected={previewView === 'timeline'}
                >
                  Timeline
                </button>
                <button
                  type="button"
                  className={previewView === 'list' ? 'active' : ''}
                  onClick={() => setPreviewView('list')}
                  data-testid="lumiere-view-list"
                  role="tab"
                  aria-selected={previewView === 'list'}
                >
                  Detalle
                </button>
              </div>
            </div>

            {previewView === 'timeline' ? (
              <div className="lumiere-timeline" data-testid="lumiere-timeline">
                {plan.nodes.map((n, idx) => {
                  const category = n.type === 'outputPreview' ? 'output' : (n.type.startsWith('upload') ? 'input' : 'process');
                  const fallback = n.data?.prompt
                    ? (n.data.prompt.length > 90 ? `${n.data.prompt.slice(0, 90)}…` : n.data.prompt)
                    : n.type;
                  const narrative = n.data?.narrative || fallback;
                  return (
                    <div className={`lumiere-tl-step cat-${category}`} key={n.id}>
                      <div className="lumiere-tl-node">
                        <span className="lumiere-tl-index">{idx + 1}</span>
                        <span className="lumiere-tl-type">{n.type}</span>
                      </div>
                      <div className="lumiere-tl-card">
                        <p>{narrative}</p>
                      </div>
                      {idx < plan.nodes.length - 1 && <div className="lumiere-tl-connector" aria-hidden />}
                    </div>
                  );
                })}
              </div>
            ) : (
              <ol className="lumiere-node-flow">
                {plan.nodes.map((n) => (
                  <li
                    key={n.id}
                    className={`lumiere-node-row cat-${n.type === 'outputPreview' ? 'output' : (n.type.startsWith('upload') ? 'input' : 'process')}`}
                  >
                    <div className="lumiere-node-title">
                      <code>{n.id}</code>
                      <span>{n.type}</span>
                    </div>
                    {n.data?.prompt && (
                      <div className="lumiere-node-prompt" title={n.data.prompt}>{n.data.prompt}</div>
                    )}
                    {Object.entries(n.data || {})
                      .filter(([k, v]) => k !== 'prompt' && k !== 'label' && k !== 'narrative' && typeof v !== 'object')
                      .slice(0, 4)
                      .map(([k, v]) => (
                        <span className="lumiere-node-tag" key={k}>{k}: {String(v).slice(0, 24)}</span>
                      ))}
                  </li>
                ))}
              </ol>
            )}

            <div className="lumiere-refine">
              <label>Refinar (opcional)</label>
              <div className="lumiere-refine-row">
                <input
                  type="text"
                  placeholder="Ej: hacé el video más corto y agregá música jazz"
                  value={refineText}
                  onChange={(e) => setRefineText(e.target.value)}
                  disabled={refining}
                />
                <button
                  className="lumiere-btn ghost"
                  onClick={handleRefine}
                  disabled={refining || refineText.trim().length < 2}
                  data-testid="agent-refine-btn"
                >
                  {refining ? <><Loader2 className="spin" size={12} /></> : <><RefreshCw size={12} /> Refinar</>}
                </button>
              </div>
            </div>

            {insufficient && (
              <div className="lumiere-error">
                <AlertTriangle size={13} /> Créditos insuficientes ({plan.estimated_credits} necesarios · {balance} disponibles).
              </div>
            )}
            {errorMsg && !insufficient && (
              <div className="lumiere-error"><AlertTriangle size={13} /> {errorMsg}</div>
            )}

            <div className="lumiere-panel-actions">
              <button className="lumiere-btn secondary" onClick={() => setStep('compose')}>← Editar objetivo</button>
              <button
                className="lumiere-btn primary"
                onClick={handleApply}
                disabled={busy || insufficient}
                data-testid="agent-apply"
              >
                <Check size={14} /> Aplicar flujo
              </button>
            </div>
          </div>
        )}

        {/* ── Step: Applying ─────────────────────────── */}
        {step === 'applying' && (
          <div className="lumiere-applying">
            <Wand2 size={26} className="spin" />
            <p>Aplicando el flujo al canvas…</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentComposer;
